import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import useBrandList from 'commons/hooks/use-brand-list';
import dayjs from 'dayjs';
import { getDempyouLang } from 'utils/getDempyouParam';
import { LANGUAGE_MAP } from 'constant/language';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import { Button, Modal, message } from 'antd';
import { generatePdfBlobFromContainer } from 'utils/generateDempyouPdf';
import { sendSignedContractEmail } from 'apis/oms';
import i18n from 'i18n';
import LOCALS from 'commons/locals';

export enum DempyouType {
  // 買取伝票（お客様控え）
  recycle_customer = 'recycle_customer',
  // 買取伝票
  recycle_store = 'recycle_store',

  // 委託伝票（お客様控え）
  sell_customer = 'sell_customer',
  // 委託伝票
  sell_store = 'sell_store',
}

export const DempyouTypeNameMap = {
  [DempyouType.recycle_customer]: {
    [LANGUAGE_MAP.JA]: '買取伝票（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Repurchase Receipt(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '二手商品交易合同（副本）',
    [LANGUAGE_MAP.FR]: 'Reçu de rachat (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '二手商品交易合同（副本）',
  },
  [DempyouType.recycle_store]: {
    [LANGUAGE_MAP.JA]: '買取伝票',
    [LANGUAGE_MAP.EN]: 'Repurchase Receipt',
    [LANGUAGE_MAP.ZH_CN]: '二手商品交易合同',
    [LANGUAGE_MAP.FR]: 'Reçu de rachat',
    [LANGUAGE_MAP.ZH_TW]: '二手商品交易合同',
  },
  [DempyouType.sell_customer]: {
    [LANGUAGE_MAP.JA]: '委託伝票（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Consignment Receipt(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '二手商品交易合同（副本）',
    [LANGUAGE_MAP.FR]: 'Reçu de consignation (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '二手商品交易合同（副本）',
  },
  [DempyouType.sell_store]: {
    [LANGUAGE_MAP.JA]: '委託伝票',
    [LANGUAGE_MAP.EN]: 'Consignment Receipt',
    [LANGUAGE_MAP.ZH_CN]: '二手商品交易合同',
    [LANGUAGE_MAP.FR]: 'Reçu de consignation',
    [LANGUAGE_MAP.ZH_TW]: '二手商品交易合同',
  },
};

export const ReturnReceiptDempyouTypeNameMap = {
  [DempyouType.recycle_customer]: {
    [LANGUAGE_MAP.JA]: '返品書（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Return Receipt(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '退货单据（副本）',
    [LANGUAGE_MAP.FR]: 'Reçu de retour (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '退货单据（副本）',
  },
  [DempyouType.recycle_store]: {
    [LANGUAGE_MAP.JA]: '返品書',
    [LANGUAGE_MAP.EN]: 'Return Receipt',
    [LANGUAGE_MAP.ZH_CN]: '退货单据',
    [LANGUAGE_MAP.FR]: 'Reçu de retour',
    [LANGUAGE_MAP.ZH_TW]: '退货单据',
  },
  [DempyouType.sell_customer]: {
    [LANGUAGE_MAP.JA]: '返品書（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Return Receipt(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '退货单据（副本）',
    [LANGUAGE_MAP.FR]: 'Reçu de retour (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '退货单据（副本）',
  },
  [DempyouType.sell_store]: {
    [LANGUAGE_MAP.JA]: '返品書',
    [LANGUAGE_MAP.EN]: 'Return Receipt',
    [LANGUAGE_MAP.ZH_CN]: '退货单据',
    [LANGUAGE_MAP.FR]: 'Reçu de retour',
    [LANGUAGE_MAP.ZH_TW]: '退货单据',
  },
};

export const SettlementStatementDempyouTypeNameMap = {
  [DempyouType.recycle_customer]: {
    [LANGUAGE_MAP.JA]: 'ご清算書（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Settlement Statement(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '结算单（副本）',
    [LANGUAGE_MAP.FR]: 'Relevé de règlement (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '结算单（副本）',
  },
  [DempyouType.recycle_store]: {
    [LANGUAGE_MAP.JA]: 'ご清算書',
    [LANGUAGE_MAP.EN]: 'Settlement Statement',
    [LANGUAGE_MAP.ZH_CN]: '结算单',
    [LANGUAGE_MAP.FR]: 'Relevé de règlement',
    [LANGUAGE_MAP.ZH_TW]: '结算单',
  },
  [DempyouType.sell_customer]: {
    [LANGUAGE_MAP.JA]: 'ご清算書（お客様控え）',
    [LANGUAGE_MAP.EN]: 'Settlement Statement(Copy)',
    [LANGUAGE_MAP.ZH_CN]: '结算单（副本）',
    [LANGUAGE_MAP.FR]: 'Relevé de règlement (Copie)',
    [LANGUAGE_MAP.ZH_TW]: '结算单（副本）',
  },
  [DempyouType.sell_store]: {
    [LANGUAGE_MAP.JA]: 'ご清算書',
    [LANGUAGE_MAP.EN]: 'Settlement Statement',
    [LANGUAGE_MAP.ZH_CN]: '结算单',
    [LANGUAGE_MAP.FR]: 'Relevé de règlement',
    [LANGUAGE_MAP.ZH_TW]: '结算单',
  },
};

export type DempyouPrintDto = {
  code: string;
  date: string;
  payType: string;
  staff: string;
  userInfo: string;
  memberId: number;
  dempyouType: DempyouType;
  totalPrice: string | number;
  prints?: string; // 是否打印
  productList: {
    productName: string;
    productPrice: string | number;
    guestRemarks: string;
    code: string;
    productSn: string;
    brandName: string;
    productId: number;
    isAuthentic?: number;
  }[];
  submissionId?: number;
  storeId?: number;
  isReturnReceipt?: boolean;
  isSettlementStatement?: boolean;
  isTradeIn?: number;
  payoutDetail?: string;
  signatureImage?: string;
};

const DempyouPrint = () => {
  const [state, setState] = useState<DempyouPrintDto[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const omsRecycleOrderIdsRef = useRef<string[]>([]);
  const pdfFileNameRef = useRef('signed-contract.pdf');
  const [sending, setSending] = useState(false);
  const [isSendContractMode, setIsSendContractMode] = useState(false);
  const { SourceBrandList } = useBrandList();
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get('key');
    setIsSendContractMode(urlParams.get('sendContract') === '1');
    omsRecycleOrderIdsRef.current =
      urlParams.get('orderIds')?.split(',').filter(Boolean) || [];
    pdfFileNameRef.current =
      urlParams.get('filename') || pdfFileNameRef.current;
    let bodyParse: DempyouPrintDto[] = [];

    if (key) {
      const data = localStorage.getItem(key);
      if (data) {
        bodyParse = JSON.parse(data) as DempyouPrintDto[];
        // 立即清除缓存
        localStorage.removeItem(key);
      }
    }

    setState(bodyParse);
    const sendContract = urlParams.get('sendContract') === '1';
    if (bodyParse.length > 0 && bodyParse[0].prints && !sendContract) {
      setTimeout(() => {
        window.print();
        window.close();
      }, 666);
    }
  }, []);

  const handleSendContract = useCallback(async () => {
    const orderIds = omsRecycleOrderIdsRef.current;
    if (!orderIds.length) {
      message.error(String(i18n.t(LOCALS.signed_contract_missing_order_ids)));
      return;
    }

    Modal.confirm({
      title: i18n.t(LOCALS.signed_contract_confirm_send_title),
      content: i18n.t(LOCALS.signed_contract_confirm_send_content),
      okText: i18n.t(LOCALS.signed_contract_confirm_send),
      cancelText: i18n.t(LOCALS.cancel),
      onOk: async () => {
        setSending(true);
        try {
          const { blob, filename } = await generatePdfBlobFromContainer(
            containerRef.current,
            pdfFileNameRef.current,
          );
          const formData = new FormData();
          formData.append('file', blob, filename);
          formData.append('omsRecycleOrderIds', orderIds.join(','));
          await sendSignedContractEmail(formData);
          message.success(
            String(i18n.t(LOCALS.signed_contract_email_sent_success)),
          );
          window.close();
        } catch (error) {
          console.error('send signed contract failed', error);
          message.error(String(i18n.t(LOCALS.signed_contract_send_failed)));
          throw error;
        } finally {
          setSending(false);
        }
      },
    });
  }, []);

  const storeId = useMemo(() => {
    if (state.length === 0) return SHOP_MAP.GINZA;
    return state[0].storeId || SHOP_MAP.GINZA;
  }, [state]);

  let language = getDempyouLang(storeId);
  // 法文单独处理, 为啥没在getDempyouLang中处理是有原因的
  // 因为法文没有对应的语言包, 所以需要单独处理
  if (storeId === SHOP_MAP.FRANCE) {
    language = LANGUAGE_MAP.FR;
  }

  const isSgShop = useMemo(() => {
    return (
      storeId === SHOP_MAP.SINGAPORE ||
      storeId === SHOP_MAP.SINGAPORE_ASU_GST ||
      storeId === SHOP_MAP.SINGAPORE_GX
    );
  }, [storeId]);

  const isJpShop = useMemo(() => {
    return storeId === SHOP_MAP.GINZA || storeId === SHOP_MAP.UMEDA;
  }, [storeId]);

  const currencySymbol = useMemo(() => {
    return getShopConfig(storeId).currency;
  }, [storeId]);

  const storeInfoEl = useMemo(() => {
    if (storeId === SHOP_MAP.HONGKONG) {
      return (
        <div className="text-right">
          ASU Company Limited
          <br />
          HONGKONG
          <br />
          G/F, Pacific House, 20B
          <br />
          Queen's Road Central, Hong Kong
          <br />
          Email &nbsp;&nbsp;info.hk@asubrand.jp
          <br />
          TEL &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;+852 2662 9721
        </div>
      );
    }

    if (storeId === SHOP_MAP.SINGAPORE) {
      return (
        <div className="text-right">
          ASU COMPANY PTE. LTD.
          <br />
          391B ORCHARD ROAD, #23-05, NGEE ANN CITY, SINGAPORE 238874
          <br />
          UEN: 201939838Z
          <br />
          EMAIL: info.sg@asubrand.jp
          <br />
          TEL: +65 6530 3529
        </div>
      );
    }

    if (storeId === SHOP_MAP.GINZA) {
      return (
        <div className="text-right">
          ASU BRAND 株式会社
          <br />
          GINZA XIAOMA
          <br />
          東京都中央区銀座1丁目8-21
          <br />
          第21中央ビル1F・2F
          <br />
          TEL 03-6264-5267
          <br />
          登録番号:T6011101072905
        </div>
      );
    }

    if (storeId === SHOP_MAP.SHANG_HAI) {
      return (
        <div className="text-right">
          上海市黄浦区淮海中路222号607室
          <br />
          XIAOMA 上海
          <br />
          电话&nbsp;&nbsp;+86 153 0162 1883
        </div>
      );
    }

    if (storeId === SHOP_MAP.UMEDA) {
      return (
        <div className="text-right">
          ASU BRAND 株式会社
          <br />
          XIAOMA梅田店
          <br />
          大阪府大阪市北区梅田1丁目8-17
          <br />
          大阪第一生命ビル1階
          <br />
          TEL&nbsp;&nbsp;06-6467-8153
          <br />
          登録番号:T6011101072905
        </div>
      );
    }

    if (storeId === SHOP_MAP.DUBAI) {
      return (
        <div className="text-right">
          ASU Company Limited
          <br />
          DUBAI
          <br />
          Room102，32 Marasi Drive Street
          <br />
          The Binary by Omniyat，Business Bay
          <br />
          Dubai United Arab Emirates
          <br />
          Email &nbsp;&nbsp;dubai@ginzaxiaoma.com
          <br />
          <br />
          TEL &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;00971 4 5540680
        </div>
      );
    }

    if (storeId === SHOP_MAP.FRANCE) {
      return (
        <div className="text-right">
          XIAOMA PARIS
          <br />
          PARIS
          <br />
          9 Rue de la Paix, 75002
          <br />
          Email : info.fr@ginzaxiaoma.com
          <br />
          Tél. : +33 0140280349
        </div>
      );
    }

    if (storeId === SHOP_MAP.NEW_YORK) {
      return (
        <div className="text-right">
          XIAOMA NEW YORK
          <br />
          Trump Tower, Suite 1901
          <br />
          725 Fifth Avenue
          <br />
          New York, NY 10022
          <br />
          United States
          <br />
          TEL &nbsp;&nbsp;+1 9177488152
          <br />
          Mon-Sat 10:00-19:00
        </div>
      );
    }
  }, [storeId]);

  const storeName = useMemo(() => {
    return getShopConfig(storeId).companyName;
  }, [storeId]);

  const A4Render = useCallback(
    (data: DempyouPrintDto, i: number) => {
      const len = (state[0]?.productList || []).filter(
        (item) => item.isAuthentic !== 0,
      ).length;
      const printableProductList = data.productList.filter(
        (item) => item.isAuthentic !== 0,
      );
      const printableTotalPrice = printableProductList.reduce((total, item) => {
        const price = Number(item.productPrice) || 0;
        return total + price;
      }, 0);
      const tradeInSuffix = data.isTradeIn
        ? {
            [LANGUAGE_MAP.EN]: 'Trade-In',
            [LANGUAGE_MAP.JA]: '下取り',
            [LANGUAGE_MAP.ZH_CN]: '置换',
            [LANGUAGE_MAP.FR]: 'Reprise',
            [LANGUAGE_MAP.ZH_TW]: '置换',
          }[language] || 'Trade-In'
        : '';
      const payTypeDisplay = data.isTradeIn ? tradeInSuffix : data.payType;
      const headList = () => {
        const list = [
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Receipt No.',
              [LANGUAGE_MAP.JA]: '伝票番号',
              [LANGUAGE_MAP.ZH_CN]: '单据编号',
              [LANGUAGE_MAP.FR]: 'Numéro de Reçu',
              [LANGUAGE_MAP.ZH_TW]: '单据编号',
            }[language],
            value: data.submissionId ? data.submissionId.toString() : data.code,
            hidden: len > 1 && !data.submissionId,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Date',
              [LANGUAGE_MAP.JA]: '受付日',
              [LANGUAGE_MAP.ZH_CN]: '日期',
              [LANGUAGE_MAP.FR]: 'Date',
              [LANGUAGE_MAP.ZH_TW]: '日期',
            }[language],
            value: data.date,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Accountable',
              [LANGUAGE_MAP.JA]: '担当者',
              [LANGUAGE_MAP.ZH_CN]: '担当者',
              [LANGUAGE_MAP.FR]: 'Responsable',
              [LANGUAGE_MAP.ZH_TW]: '担当者',
            }[language],
            value: data.staff,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Transaction',
              [LANGUAGE_MAP.JA]: 'ご清算区分',
              [LANGUAGE_MAP.ZH_CN]: '结算方式',
              [LANGUAGE_MAP.FR]: 'Transaction',
              [LANGUAGE_MAP.ZH_TW]: '结算方式',
            }[language],
            value: payTypeDisplay,
            hidden: data.isReturnReceipt,
          },
        ].filter((d) => !d.hidden);

        return list;
      };

      const isStoreCopy = [
        DempyouType.recycle_store,
        DempyouType.sell_store,
      ].includes(data.dempyouType);

      const isCustomerCopy = [
        DempyouType.recycle_customer,
        DempyouType.sell_customer,
      ].includes(data.dempyouType);
      const showSignatureArea = isStoreCopy || !!data.signatureImage;
      const payoutDetailLines =
        data.payoutDetail && isSgShop ? data.payoutDetail.split('\n') : [];

      const priceArr = (() => {
        const el1 = [
          {
            [LANGUAGE_MAP.EN]: 'Sub Total',
            [LANGUAGE_MAP.JA]: '小計',
            [LANGUAGE_MAP.ZH_CN]: '小记',
            [LANGUAGE_MAP.FR]: 'Sous-total',
            [LANGUAGE_MAP.ZH_TW]: '小計',
          }[language],
          currencySymbol + ' ' + printableTotalPrice.toLocaleString(),
        ];
        const el2 = [
          '消費税10%対象 内税額',
          currencySymbol +
            ' ' +
            Math.floor(printableTotalPrice / 11).toLocaleString(),
        ];
        const el3 = [
          {
            [LANGUAGE_MAP.EN]: 'Total',
            [LANGUAGE_MAP.JA]: '総合計',
            [LANGUAGE_MAP.ZH_CN]: '合计',
            [LANGUAGE_MAP.FR]: 'Total',
            [LANGUAGE_MAP.ZH_TW]: '総合計',
          }[language],
          currencySymbol + ' ' + printableTotalPrice.toLocaleString(),
        ];

        if (language === LANGUAGE_MAP.JA) return [el1, el2, el3];

        return [el1, el3];
      })();

      const receiptTitle = (() => {
        if (data.isReturnReceipt) {
          return ReturnReceiptDempyouTypeNameMap[data.dempyouType][language];
        }

        if (data.isSettlementStatement) {
          return SettlementStatementDempyouTypeNameMap[data.dempyouType][
            language
          ];
        }

        return DempyouTypeNameMap[data.dempyouType][language];
      })();

      const tips1El = (() => {
        if (storeId === SHOP_MAP.FRANCE) {
          return (
            <div className="border-l border-r px-2 py-2 border-b shrink-0">
              -En cas de litige, {storeName} se réserve le droit de décision
              finale.
            </div>
          );
        }

        if (
          isSgShop ||
          storeId === SHOP_MAP.HONGKONG ||
          storeId === SHOP_MAP.DUBAI
        ) {
          return (
            <div className="border-l border-r px-2 py-2 border-b shrink-0">
              -In event of any disputes, {storeName} reserves the right of final
              decision.
            </div>
          );
        }

        if (storeId === SHOP_MAP.SHANG_HAI) {
          return (
            <div className="border-l border-r px-2 py-2 border-b shrink-0">
              -如有任何争议，{storeName}保留最终决定权
            </div>
          );
        }
      })();

      const tips2El = (() => {
        if (isJpShop) {
          return (
            <>
              ご契約後のお買戻しはお受付できませんのでご了承ください。
              <br />
              ご契約後、商品が不正商品と発覚した場合、全額返金いただく場合があります。
            </>
          );
        }
        if (storeId === SHOP_MAP.FRANCE) {
          return (
            <>
              Aucun remboursement après le rachat.
              <br />
              Si le produit s&apos;avère être une contrefaçon ou un article
              volé,
              <br />
              le client est tenu de rembourser. {storeName} se réserve le droit
              <br />
              d&apos;engager des poursuites judiciaires.
            </>
          );
        }

        if (
          isSgShop ||
          storeId === SHOP_MAP.HONGKONG ||
          storeId === SHOP_MAP.DUBAI
        ) {
          return (
            <>
              No refund after repurchase.
              <br />
              If the product was found to be counterfeit or stolen item,
              <br />
              the customer is responsible to refund Asubrand Company Limited
              <br />
              reserve the right to pursue legal action.
            </>
          );
        }

        if (storeId === SHOP_MAP.SHANG_HAI) {
          return (
            <>
              契约成立后不予退款。
              <br />
              如发现商品为假货或赃物，
              <br />
              客户有责任向 晓马（上海）贸易有限公司 退款，
              <br />
              本公司保留追究法律责任的权利。
            </>
          );
        }
      })();

      const tips3El = (() => {
        if (isJpShop) {
          return (
            <>
              ○ 本商品は、お預かり日より１か月以上お預かりするものとし、
              <br />
              １か月未満での委託販売中止は基本出来ないものとします（要ご相談）
              <br />
              ○ 本商品の売却が成立した場合、決済確定後にお知らせいたします。
              <br />
              お知らせ後はご相談によりお振込みまたは現金にて表記の金額をお支払いいたします。
            </>
          );
        }

        if (storeId === SHOP_MAP.FRANCE) {
          return (
            <>
              La période de consignation est de 1 mois.
              <br />
              Veuillez nous contacter si vous souhaitez annuler la consignation
              à l&apos;avance.
              <br />
              Une fois la vente conclue, nous vous contacterons immédiatement et
              transférerons
              <br />
              le montant convenu sur le compte désigné.
            </>
          );
        }

        if (
          isSgShop ||
          storeId === SHOP_MAP.HONGKONG ||
          storeId === SHOP_MAP.DUBAI
        ) {
          return (
            <>
              Consignment period is 1 months.
              <br />
              Please contact us if you want to cancel consignment in advance.
              <br />
              Once the consignment is sold, we will contact you immediately and
              transfer
              <br />
              the agreement amount to your designated account.
            </>
          );
        }

        if (storeId === SHOP_MAP.SHANG_HAI) {
          return (
            <>
              寄卖期为1个月。
              <br />
              如需提前取消寄卖，请与我们联系。
              <br />
              一旦商品售出，我们会立即与您联系，
              <br />
              并将协议金额转账至您指定的账户。
            </>
          );
        }
      })();

      const tips4El = (() => {
        if (isJpShop) {
          return (
            <>
              当店では商品の取扱いには細心の注意を払っておりますが、
              万一お届けいたしました商品に不備がございました場合には、商品到着後
              すぐにお電話、またはメールにてご連絡ください。
              <br />
              また、不良品以外の返品・交換は致しかねますので、
              あらかじめご了承ください下さいますようお願い申し上げます。
              <br />
              <br />
              またのご利用をお待ちしております。ありがとうございました。
            </>
          );
        }

        if (storeId === SHOP_MAP.FRANCE) {
          return (
            <>
              Nous apportons le plus grand soin à la manipulation des produits.
              Toutefois, en cas de défaut, veuillez nous contacter par téléphone
              ou par e-mail dès réception.
              <br />
              Veuillez noter que nous n&apos;acceptons pas les retours ou
              échanges pour d&apos;autres raisons que les défauts du produit.
              Merci de votre compréhension.
              <br />
              <br />
              Nous espérons vous servir à nouveau.
            </>
          );
        }

        if (
          isSgShop ||
          storeId === SHOP_MAP.HONGKONG ||
          storeId === SHOP_MAP.DUBAI
        ) {
          return (
            <>
              We take the utmost care in handling the products. However, in the
              unlikely event that you receive a defective item, please contact
              us by phone or email immediately after the product arrives.
              <br />
              Please note that we do not accept returns or exchanges for reasons
              other than product defects. Thank you for your understanding.
              <br />
              <br />
              We look forward to serving you again.
            </>
          );
        }

        if (storeId === SHOP_MAP.SHANG_HAI) {
          return (
            <>
              我们会以最谨慎的态度处理商品。但若您收到的商品出现瑕疵，
              请在商品送达后立即通过电话或电子邮件与我们联系。
              <br />
              请注意，除商品本身存在瑕疵外，我们不接受其他原因的退换货申请。感谢您的理解与配合。
              <br />
              <br />
              期待再次为您服务。
            </>
          );
        }
      })();

      return (
        <div
          key={i}
          className={classNames(
            'w-[594.3pt] h-[840.51pt] bg-white py-4 px-10 flex flex-col text-black',
            styles.dempyou,
          )}
        >
          <div
            className={classNames(
              'px-1 mb-12 shrink-0',
              storeId === SHOP_MAP.HONGKONG
                ? 'bg-white text-black border border-black'
                : 'bg-black text-white',
            )}
            // https://blog.csdn.net/weixin_45122120/article/details/103269099
            // https://developer.mozilla.org/zh-CN/docs/Web/CSS/print-color-adjust
            style={{ printColorAdjust: 'exact' }}
          >
            {storeName.toUpperCase()}
          </div>

          <div className="mb-6 shrink-0">
            <div className="flex items-center relative">
              <div>
                {headList().map(({ label, value }, index) => {
                  return (
                    <div key={index} className="border-b mb-1">
                      <span className="italic w-24 inline-block font-semibold">
                        {label}
                      </span>
                      <span>{value}</span>
                    </div>
                  );
                })}
              </div>

              <h1
                className={classNames(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                  language === LANGUAGE_MAP.EN ? 'text-[22px]' : '',
                )}
              >
                {receiptTitle}
              </h1>

              {isStoreCopy && (isJpShop || storeId === SHOP_MAP.HONGKONG) && (
                <div className="absolute right-4 -top-4 border px-4 py-2">
                  {language === LANGUAGE_MAP.EN
                    ? 'Consignment method'
                    : '買取方法'}
                  : 店頭
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center mb-4 justify-between shrink-0">
            <div className="border p-px basis-[68%] shrink-0">
              <div className="border p-2">
                {[...data.userInfo.split('\n'), ...payoutDetailLines].map(
                  (rawText, index) => {
                    let text = rawText || '';

                    if (isStoreCopy && index === 0) {
                      text = `${text} 、${
                        {
                          [LANGUAGE_MAP.EN]: 'Customer',
                          [LANGUAGE_MAP.JA]: '顧客',
                          [LANGUAGE_MAP.ZH_CN]: '顾客',
                          [LANGUAGE_MAP.FR]: 'Client',
                          [LANGUAGE_MAP.ZH_TW]: '顧客',
                        }[language]
                      } NO.${data.memberId}`;
                    }

                    return (
                      <div key={index} className="border-b mb-2 h-4">
                        {text}
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {isCustomerCopy && storeInfoEl}
          </div>

          <div className="flex-1 flex flex-col">
            <table className="flex-1">
              <thead>
                <tr
                  className={classNames(
                    storeId === SHOP_MAP.HONGKONG
                      ? 'bg-white text-black'
                      : 'bg-black text-white',
                  )}
                  style={{ printColorAdjust: 'exact' }}
                >
                  <th className="p-2 w-10 font-normal">
                    {len === 1 || (data.submissionId && len > 1)
                      ? ''
                      : '伝票番号'}
                  </th>
                  <th className="p-2 w-20 font-normal">
                    {
                      {
                        [LANGUAGE_MAP.EN]: 'Brand',
                        [LANGUAGE_MAP.JA]: 'ブランド',
                        [LANGUAGE_MAP.ZH_CN]: '品牌',
                        [LANGUAGE_MAP.FR]: 'Marque',
                        [LANGUAGE_MAP.ZH_TW]: '品牌',
                      }[language]
                    }
                  </th>
                  <th className="p-2 font-normal">
                    {
                      {
                        [LANGUAGE_MAP.EN]: 'Description',
                        [LANGUAGE_MAP.JA]: '商品名',
                        [LANGUAGE_MAP.ZH_CN]: '商品名',
                        [LANGUAGE_MAP.FR]: 'Description',
                        [LANGUAGE_MAP.ZH_TW]: '商品名',
                      }[language]
                    }
                  </th>
                  {!data.isReturnReceipt && (
                    <th className="p-2 w-28 font-normal">
                      {
                        {
                          [LANGUAGE_MAP.EN]: 'Cost',
                          [LANGUAGE_MAP.JA]: '金額(税込)',
                          [LANGUAGE_MAP.ZH_CN]: '金额',
                          [LANGUAGE_MAP.FR]: 'Coût',
                          [LANGUAGE_MAP.ZH_TW]: '金額',
                        }[language]
                      }
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {printableProductList.map((d, i) => (
                  <tr className="h-4" key={i}>
                    <td className="px-2 py-1">
                      {len === 1 || (data.submissionId && len > 1)
                        ? i + 1
                        : d.code}
                    </td>
                    <td className="px-2 py-1">
                      {
                        {
                          [LANGUAGE_MAP.EN]:
                            SourceBrandList.find(
                              (dd) => dd.value === d.brandName,
                            )?.nameEn || SourceBrandList[0].nameEn,
                          [LANGUAGE_MAP.FR]:
                            SourceBrandList.find(
                              (dd) => dd.value === d.brandName,
                            )?.nameEn || SourceBrandList[0].nameEn,
                          [LANGUAGE_MAP.JA]:
                            SourceBrandList.find(
                              (dd) => dd.value === d.brandName,
                            )?.nameJa || SourceBrandList[0].nameJa,
                          [LANGUAGE_MAP.ZH_CN]:
                            SourceBrandList.find(
                              (dd) => dd.value === d.brandName,
                            )?.nameZh || SourceBrandList[0].nameZh,
                          [LANGUAGE_MAP.ZH_TW]:
                            SourceBrandList.find(
                              (dd) => dd.value === d.brandName,
                            )?.nameZh || SourceBrandList[0].nameZh,
                        }[language]
                      }
                    </td>
                    <td className="px-2 py-1">
                      {isStoreCopy && <>{d.productId}:</>}
                      {d.productName}
                      {d.guestRemarks && (
                        <>
                          <div className="border-b" />
                          {d.guestRemarks}
                        </>
                      )}
                    </td>
                    {!data.isReturnReceipt && (
                      <td className="px-2 py-1 text-right">
                        {currencySymbol +
                          ' ' +
                          Number(d.productPrice).toLocaleString()}
                      </td>
                    )}
                  </tr>
                ))}
                {Array.from({ length: 20 - printableProductList.length }).map(
                  (value, index) => {
                    return (
                      <tr className="h-4" key={index}>
                        <td className="px-2 py-1"></td>
                        <td className="px-2 py-1"></td>
                        <td className="px-2 py-1"></td>
                        {!data.isReturnReceipt && (
                          <td className="px-2 py-1"></td>
                        )}
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>

            {tips1El}

            <div className="border-l border-r px-2 py-2 shrink-0">
              {!data.isReturnReceipt ? (
                <>
                  <div className="flex justify-between">
                    <div className="text-[10px]">
                      {[
                        DempyouType.recycle_customer,
                        DempyouType.recycle_store,
                      ].includes(data.dempyouType) && tips2El}

                      {[
                        DempyouType.sell_customer,
                        DempyouType.sell_store,
                      ].includes(data.dempyouType) && tips3El}
                    </div>

                    <div className="basis-1/3">
                      {priceArr.map(([title, value], index) => {
                        return (
                          <div
                            key={index}
                            className="flex justify-between mb-1"
                          >
                            <span>{title}</span>
                            <span>{value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {showSignatureArea && (
                    <div className="mt-3">
                      <div
                        className={classNames(
                          'relative mb-1 w-1/2 min-h-[48px] flex justify-between items-end pb-1',
                          styles.borderBottomDouble,
                        )}
                      >
                        <span>
                          {
                            {
                              [LANGUAGE_MAP.EN]: 'Signature',
                              [LANGUAGE_MAP.JA]: '名前',
                              [LANGUAGE_MAP.ZH_CN]: '签名:',
                              [LANGUAGE_MAP.FR]: 'Signature',
                              [LANGUAGE_MAP.ZH_TW]: '签名:',
                            }[language]
                          }
                        </span>
                        {data.signatureImage && (
                          <img
                            src={data.signatureImage}
                            alt=""
                            className="absolute left-16 bottom-1 h-12 max-w-[180px] object-contain"
                          />
                        )}
                        {isJpShop && <span>印</span>}
                      </div>
                      {isJpShop && (
                        <div className="text-[11px]">
                          <span>
                            適格請求書（インボイス）発行事業者ではありません。
                          </span>
                          <span className="ml-2">口はい</span>
                          <span className="ml-2">口いいえ</span>
                          <span className="ml-4">適格請求畫発行事業者</span>
                          <span className="ml-2">登録番号：</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-xs">
                    {tips4El}
                    {showSignatureArea && (
                      <div className="flex justify-between mt-4">
                        <div>
                          {
                            {
                              [LANGUAGE_MAP.EN]:
                                'I have received the above item.',
                              [LANGUAGE_MAP.JA]: '上記お品物を受け取りました。',
                              [LANGUAGE_MAP.ZH_CN]: '我已收到上述商品。',
                              [LANGUAGE_MAP.FR]:
                                "J'ai reçu le(s) article(s) ci-dessus.",
                              [LANGUAGE_MAP.ZH_TW]: '我已收到上述商品。',
                            }[language]
                          }
                        </div>
                        <div
                          className={classNames(
                            'relative mb-1 w-1/2 min-h-[48px] flex justify-between items-end pb-1',
                            styles.borderBottomDouble,
                          )}
                        >
                          <span>
                            {
                              {
                                [LANGUAGE_MAP.EN]: 'Signature',
                                [LANGUAGE_MAP.JA]: '名前',
                                [LANGUAGE_MAP.ZH_CN]: '签名:',
                                [LANGUAGE_MAP.FR]: 'Signature',
                                [LANGUAGE_MAP.ZH_TW]: '签名:',
                              }[language]
                            }
                          </span>
                          {data.signatureImage && (
                            <img
                              src={data.signatureImage}
                              alt=""
                              className="absolute left-16 bottom-1 h-12 max-w-[180px] object-contain"
                            />
                          )}
                          {isJpShop && <span>印</span>}
                        </div>
                      </div>
                    )}
                    <div className="text-right">
                      {dayjs().format('YYYY/MM/DD')}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div
              className={classNames(
                'text-lg px-1 shrink-0',
                storeId === SHOP_MAP.HONGKONG
                  ? 'bg-white text-black border'
                  : 'bg-black text-white',
              )}
              style={{ printColorAdjust: 'exact' }}
            >
              {storeName.toUpperCase()}
            </div>
          </div>
        </div>
      );
    },
    [
      state,
      language,
      isSgShop,
      storeId,
      storeName,
      isJpShop,
      storeInfoEl,
      currencySymbol,
      SourceBrandList,
    ],
  );

  return (
    <>
      <div className={isSendContractMode ? 'pb-28 print:pb-0' : undefined}>
        <div ref={containerRef}>{state.map((d, i) => A4Render(d, i))}</div>
      </div>
      {isSendContractMode && state.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-4 border-t bg-white px-6 py-4 shadow-lg print:hidden">
          <span className="text-sm text-gray-600">
            {i18n.t(LOCALS.signed_contract_review_before_send)}
          </span>
          <Button onClick={() => window.close()} disabled={sending}>
            {i18n.t(LOCALS.signed_contract_close)}
          </Button>
          <Button type="primary" loading={sending} onClick={handleSendContract}>
            {i18n.t(LOCALS.signed_contract_confirm_send)}
          </Button>
        </div>
      )}
    </>
  );
};

export default DempyouPrint;
